/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  partnerApi, fmtPartnerErr, clearPartnerToken, isPartnerAuthed,
} from "../../lib/partnerApi";
import {
  ShoppingBag, Calendar, Truck, CurrencyInr, Storefront, Bell,
  SignOut, Lightning, ArrowRight, FirstAid, ShieldCheck, GearSix,
} from "@phosphor-icons/react";

const CAT_META = {
  retail:   { Icon: Storefront, color: "#0B2B66", label: "Retail" },
  health:   { Icon: FirstAid,   color: "#2D5A27", label: "Health" },
  delivery: { Icon: Truck,      color: "#C25D38", label: "Delivery" },
};

export default function PartnerDashboard() {
  const nav = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    try {
      const r = await partnerApi.get("/service-partners/me/dashboard");
      setData(r.data);
    } catch (e) {
      if (e?.response?.status === 401) {
        clearPartnerToken();
        nav("/service-partner/login", { replace: true });
      } else toast.error(fmtPartnerErr(e));
    }
    setLoading(false);
  };
  useEffect(() => {
    if (!isPartnerAuthed()) {
      nav("/service-partner/login", { replace: true });
      return;
    }
    load();
  }, []);

  const logout = () => {
    clearPartnerToken();
    nav("/service-partner/login", { replace: true });
  };

  if (loading) return <Loading/>;
  if (!data) return null;
  const p = data.partner;
  const meta = CAT_META[p.category] || CAT_META.retail;

  return (
    <div className="min-h-screen bg-[#F7F6F2]" data-testid="partner-dashboard">
      {/* Top bar */}
      <header className="bg-white border-b border-[#E5E2D9] sticky top-0 z-30">
        <div className="max-w-6xl mx-auto px-6 py-3 flex items-center gap-4">
          <Link to="/service-partner/dashboard" className="flex items-center gap-2.5">
            <div className="w-9 h-9 rounded grid place-items-center text-white" style={{ background: meta.color }}>
              <meta.Icon size={16} weight="duotone"/>
            </div>
            <div>
              <div className="font-heading text-base leading-tight">{p.name}</div>
              <div className="text-[10px] uppercase tracking-[0.2em] text-[#8A8780]">
                {meta.label} · {prettyLabel(p.subcategory)}
              </div>
            </div>
          </Link>
          <div className="ml-auto flex items-center gap-2">
            <button className="h-9 w-9 grid place-items-center text-[#5C5A55] hover:bg-[#F7F6F2] rounded">
              <Bell size={14}/>
            </button>
            <button onClick={logout} data-testid="partner-logout"
                    className="h-9 px-3 inline-flex items-center gap-1.5 text-xs text-[#5C5A55] hover:bg-[#F7F6F2] rounded">
              <SignOut size={12}/> Sign out
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-6 py-8 space-y-6">
        {/* Welcome banner */}
        <section className="bg-white rounded-xl border border-[#E5E2D9] p-6 relative overflow-hidden">
          <div className="absolute -right-12 -top-12 w-48 h-48 rounded-full opacity-5"
                style={{ background: meta.color }}/>
          <div className="relative">
            <div className="text-[10px] uppercase tracking-[0.22em] text-[#5C5A55]">
              Partner Dashboard · Phase 1
            </div>
            <h1 className="font-heading text-3xl mt-1" style={{ color: meta.color }}>
              Welcome to Rescigo Partner Portal
            </h1>
            <p className="text-sm text-[#5C5A55] mt-2 max-w-2xl leading-relaxed">
              Your account is provisioned. Catalog, bookings and payouts roll out as we
              activate each module — you'll see them appear here automatically.
            </p>
          </div>
        </section>

        {/* KPIs */}
        <section className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3"
                  data-testid="partner-kpis">
          <Kpi Icon={ShoppingBag} color="#0B2B66" label="Orders today"     v={data.kpis.orders_today}/>
          <Kpi Icon={ShoppingBag} color="#0B2B66" label="Orders · month"   v={data.kpis.orders_this_month}/>
          <Kpi Icon={CurrencyInr}  color="#2D5A27" label="Revenue · month" v={fmtINR(data.kpis.revenue_this_month)}/>
          <Kpi Icon={Storefront}   color="#7A2E18" label="Active listings"  v={data.kpis.active_listings}/>
          <Kpi Icon={Calendar}     color="#C25D38" label="Open appointments" v={data.kpis.open_appointments}/>
          <Kpi Icon={Truck}        color="#5C5A55" label="Pending delivery" v={data.kpis.pending_deliveries}/>
        </section>

        {/* Profile + Next steps */}
        <section className="grid md:grid-cols-3 gap-4">
          <div className="md:col-span-2 bg-white rounded-xl border border-[#E5E2D9] p-5">
            <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.22em] text-[#5C5A55]">
              <Lightning size={11} weight="duotone" color="#C9A227"/> Next steps
            </div>
            <ul className="mt-3 space-y-2.5">
              {(data.next_steps || []).map((s, i) => (
                <li key={i} className="flex items-start gap-3 text-sm">
                  <span className="w-6 h-6 grid place-items-center rounded-full bg-[#F7F6F2] text-[#2D5A27] text-xs font-mono flex-none">
                    {i + 1}
                  </span>
                  <span className="text-[#1A1A1A]">{s}</span>
                </li>
              ))}
            </ul>
            <div className="flex flex-wrap gap-2 mt-5 pt-4 border-t border-[#F0ECE3]">
              <Link to="/service-partner/listings" data-testid="link-listings"
                    className="inline-flex items-center gap-1.5 h-9 px-3 rounded text-xs font-medium bg-[#2D5A27] hover:bg-[#3A7033] text-white">
                <ShoppingBag size={11} weight="bold"/> Manage listings
              </Link>
              <Link to="/service-partner/orders" data-testid="link-orders"
                    className="inline-flex items-center gap-1.5 h-9 px-3 rounded text-xs font-medium border border-[#E5E2D9] text-[#5C5A55] hover:border-[#2D5A27]">
                View orders
              </Link>
              <Link to="/service-partner/deliveries"
                    className="inline-flex items-center gap-1.5 h-9 px-3 rounded text-xs font-medium border border-[#E5E2D9] text-[#5C5A55] hover:border-[#2D5A27]">
                <Truck size={11}/> Deliveries
              </Link>
              {p.category === "health" && (
                <Link to="/service-partner/health" data-testid="link-health"
                      className="inline-flex items-center gap-1.5 h-9 px-3 rounded text-xs font-medium border border-[#E5E2D9] text-[#5C5A55] hover:border-[#2D5A27]">
                  <FirstAid size={11}/> Health module
                </Link>
              )}
              {["hospital","builder","insurance"].includes(p.kind) && (
                <Link to="/service-partner/wave4" data-testid="link-wave4"
                      className="inline-flex items-center gap-1.5 h-9 px-3 rounded text-xs font-medium border border-[#E5E2D9] text-[#5C5A55] hover:border-[#2D5A27]">
                  <FirstAid size={11}/> AirQ ({p.kind})
                </Link>
              )}
            </div>
          </div>
          <div className="bg-white rounded-xl border border-[#E5E2D9] p-5">
            <div className="text-[10px] uppercase tracking-[0.22em] text-[#5C5A55]">Business profile</div>
            <div className="font-heading text-lg mt-1">{p.business_name || p.name}</div>
            <dl className="mt-3 space-y-2 text-xs">
              <Info k="Phone" v={p.phone}/>
              <Info k="Email" v={p.email}/>
              <Info k="GST" v={p.gst_no || "—"}/>
              <Info k="Address" v={p.address || "—"}/>
              <Info k="Service radius" v={`${p.service_radius_km} km`}/>
              <Info k="Commission" v={`${p.commission_pct}%`}/>
              <Info k="Hours" v={p.hours_open ? `${p.hours_open} – ${p.hours_close}` : "Not set"}/>
            </dl>
            <Link to="/service-partner/account"
                  className="mt-4 inline-flex items-center gap-1.5 text-xs text-[#2D5A27] hover:underline">
              <GearSix size={11}/> Manage account <ArrowRight size={10}/>
            </Link>
          </div>
        </section>

        <footer className="text-[11px] text-[#8A8780] flex items-center gap-1.5 pt-2">
          <ShieldCheck size={11}/> All access audit-logged · Two-step verification active.
        </footer>
      </main>
    </div>
  );
}

const Kpi = ({ Icon, color, label, v }) => (
  <div className="bg-white border border-[#E5E2D9] rounded-md p-4">
    <Icon size={14} weight="duotone" color={color}/>
    <div className="font-heading text-2xl mt-2" style={{ color }}>{v ?? "—"}</div>
    <div className="text-[10px] uppercase tracking-[0.18em] text-[#8A8780] mt-0.5">{label}</div>
  </div>
);
const Info = ({ k, v }) => (
  <div className="grid grid-cols-3 gap-2">
    <dt className="col-span-1 text-[#8A8780] uppercase text-[10px] tracking-[0.15em]">{k}</dt>
    <dd className="col-span-2 text-[#1A1A1A] break-words">{v}</dd>
  </div>
);
const Loading = () => (
  <div className="min-h-screen grid place-items-center bg-[#F7F6F2] text-sm text-[#5C5A55]">
    Loading partner dashboard…
  </div>
);

function fmtINR(n) {
  if (n == null) return "—";
  return `₹${Number(n).toLocaleString("en-IN", { maximumFractionDigits: 0 })}`;
}
function prettyLabel(s) {
  if (!s) return "";
  return s.split("_").map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(" ");
}
